import { PlusOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Table } from 'antd'
import { Dispatch, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useIdentityServiceGetProfilesListEndpoint } from 'src/api/queries'
import { Permissions } from 'src/api/requests'
import {
  RoleSelect,
  PageBreadcrumbs,
  SearchBar,
  Select,
  InternalInstitutionsSelect
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { useConfig } from '../hooks'

export const UsersPage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()

  const { hasPermission, userData } = useUser()

  const isAdmin = hasPermission(Permissions.IDENTITY_USERS_VIEW_ALL)

  const { columns, isDeleting, orderingOptions, authenticationTypes } =
    useConfig(!!isAdmin)

  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [institution, setInstitution] = useState('')
  const [hasMPassConnection, setHasMpassConection] = useState<number>()

  const [ordering, setOrdering] = useState('')

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(hasMPassConnection && {
      hasMPassConnection: Boolean(hasMPassConnection)
    }),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!role && {
      role
    }),
    ...(!!institution && {
      legalEntity: institution
    }),
    ...(!isAdmin && {
      legalEntity: userData?.currentLegalEntityId
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering),
      sortDirection: getOrderingDirection(ordering)
    })
  }

  const { data, isLoading } =
    useIdentityServiceGetProfilesListEndpoint(queryParams)

  const onFilterChange = (val: string, setState: Dispatch<string>) => {
    setState(val)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('menu.users')}
      items={[
        {
          title: t('menu.users')
        }
      ]}
    >
      <Flex gap={16} justify="space-between" wrap="wrap">
        <Flex wrap="wrap" gap={16}>
          <SearchBar onSearch={(val) => onFilterChange(val, setSearch)} />
          {isAdmin && (
            <InternalInstitutionsSelect
              allowClear
              style={{
                width: 250
              }}
              onClear={() => onFilterChange('', setInstitution)}
              onChange={(val) => onFilterChange(val, setInstitution)}
            />
          )}

          <RoleSelect
            allowClear
            style={{
              width: 250
            }}
            legalEntityId={String(userData?.currentLegalEntityId)}
            onClear={() => onFilterChange('', setRole)}
            onChange={(val) => onFilterChange(val, setRole)}
          />

          <Select
            options={authenticationTypes}
            placeholder={t('users.authentication_type')}
            onSelect={(val: number) => setHasMpassConection(val)}
            allowClear
            onClear={() => setHasMpassConection(undefined)}
          />
        </Flex>

        <Flex gap={16} wrap="wrap">
          <Select
            width={188}
            options={orderingOptions}
            onSelect={(val) => onFilterChange(val, setOrdering)}
          />

          {isAdmin && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => navigate(paths.users.create)}
              type="primary"
            >
              {t('users.create_new_user')}
            </Button>
          )}
        </Flex>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        columns={columns}
        loading={isLoading || isDeleting}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
